import React, { useState, useEffect, useCallback } from 'react';
import { useSocket } from '../context/SocketContext';
import { API, ax, btn, inp } from './adminHelpers';

const STATUS_COLOR = {
  pending_signature: '#f5a623', contract_signed: '#9F5CFF',
  payment_received: '#27C85A', confirmed: '#27C85A', cancelled: '#E34364',
};
const TABS_FILTER = ['all', 'pending_signature', 'contract_signed', 'payment_received', 'confirmed', 'cancelled'];

function BookingRow({ b, getAddonsList, setSelectedBookingDetails, openExtras, markComplete, removeBooking, setDeclineModal, setDeclineMsg, requestBalance, sendInvoice, onEditBooking, openAssignModal }) {
  const [hovered, setHovered] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  
  const addonsText = getAddonsList(b);

  const handleRequestBalance = async () => {
    setRequesting(true);
    await requestBalance(b);
    setRequesting(false);
  };

  const handleSendInvoice = async () => {
    setSendingInvoice(true);
    await sendInvoice(b);
    setSendingInvoice(false);
  };

  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        background: hovered ? 'rgba(255,255,255,0.02)' : 'transparent',
        transition: 'background 0.2s',
      }}
    >
      {/* Booking ID */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#fff', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', padding: '4px 8px', borderRadius: 6 }}>
          BKG-{String(b.id).padStart(5, '0')}
        </span>
      </td>

      {/* Customer Info */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{b.customer?.name || 'Unknown'}</div>
        <div style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2 }}>{b.customer?.email}</div>
        {b.phone_number && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>📞 {b.phone_number}</div>}
      </td>

      {/* Event Details */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>📅 {b.event_date}</div>
        <div style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2 }}>🕒 {b.start_time} - {b.end_time}</div>
        <div style={{ fontSize: 11, color: '#9F5CFF', marginTop: 2, fontWeight: 600 }}>Duration: {b.duration_hours ? `${b.duration_hours}h` : '—'}</div>
      </td>

      {/* Venue / Strip Wordings */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        {b.venue_name && <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>🏛️ {b.venue_name}</div>}
        {b.strip_wordings && (
          <div style={{ fontSize: 12, color: '#f5a623', fontWeight: 600 }} title="Strip Wordings">
            💬 "{b.strip_wordings}"
          </div>
        )}
        {b.venue_address && (
          <div 
            style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} 
            title={b.venue_address}
          >
            📍 {b.venue_address}
          </div>
        )}
      </td>

      {/* Service Details & Price */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{b.booth_type}</div>
        <div style={{ fontSize: 11, color: '#f5a623', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>🎞️</span>
          <span style={{ fontWeight: 600 }}>{b.photo_strip_style || '2×6 (Vertical)'}</span>
        </div>
        <div style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2 }}>Backdrop: {b.backdrop?.name || 'None/TBD'}</div>
        {addonsText !== 'None' && (
          <div style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={addonsText}>
            Add-ons: {addonsText}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#27C85A', fontWeight: 700 }}>
            ${parseFloat(b.quoted_price || 0).toFixed(2)}
          </span>
          <span style={{ fontSize: 10, color: '#a0a0b8', fontWeight: 'normal' }}>({b.payment_type || '—'})</span>
          {b.is_tax_exempt && (
            <span style={{
              fontSize: 9,
              fontWeight: 800,
              background: 'rgba(39,200,90,0.15)',
              color: '#27C85A',
              border: '1px solid rgba(39,200,90,0.3)',
              padding: '1px 6px',
              borderRadius: 4,
              letterSpacing: 0.5,
              textTransform: 'uppercase'
            }}>
              Tax Free
            </span>
          )}
        </div>
      </td>

      {/* Status */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <span style={{
          display: 'inline-block',
          background: `${STATUS_COLOR[b.status] || '#E34364'}15`,
          border: `1px solid ${STATUS_COLOR[b.status] || '#E34364'}44`,
          color: STATUS_COLOR[b.status] || '#E34364',
          fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 50, textTransform: 'uppercase'
        }}>
          {b.status.replace('_', ' ')}
        </span>

        {/* Organizer Assignment Tag */}
        <div style={{ marginTop: 6 }}>
          {b.organizer ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 11, color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ color: '#00D2FF' }}>👤</span> {b.organizer.name}
              </div>
              <span style={{
                display: 'inline-block',
                fontSize: 9,
                fontWeight: 700,
                textTransform: 'uppercase',
                padding: '1px 6px',
                borderRadius: 4,
                width: 'fit-content',
                background: b.assignment_status === 'completed'
                  ? 'rgba(39,200,90,0.15)'
                  : b.assignment_status === 'in_progress'
                  ? 'rgba(159,92,255,0.2)'
                  : b.assignment_status === 'accepted'
                  ? 'rgba(0,210,255,0.15)'
                  : 'rgba(245,166,35,0.15)',
                color: b.assignment_status === 'completed'
                  ? '#27C85A'
                  : b.assignment_status === 'in_progress'
                  ? '#9F5CFF'
                  : b.assignment_status === 'accepted'
                  ? '#00D2FF'
                  : '#f5a623',
                border: `1px solid ${
                  b.assignment_status === 'completed'
                    ? 'rgba(39,200,90,0.3)'
                    : b.assignment_status === 'in_progress'
                    ? 'rgba(159,92,255,0.4)'
                    : b.assignment_status === 'accepted'
                    ? 'rgba(0,210,255,0.3)'
                    : 'rgba(245,166,35,0.3)'
                }`
              }}>
                {b.assignment_status ? b.assignment_status.replace('_', ' ') : 'assigned'}
              </span>
            </div>
          ) : (
            <button
              onClick={() => openAssignModal && openAssignModal(b)}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px dashed rgba(255,255,255,0.15)',
                borderRadius: 4,
                padding: '2px 6px',
                color: '#7c7c9c',
                fontSize: 10,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              👤 + Assign
            </button>
          )}
        </div>
      </td>

      {/* Actions */}
      <td 
        style={{ padding: '16px 18px', verticalAlign: 'middle', position: 'relative' }}
        onMouseLeave={() => setMenuOpen(false)}
      >
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            ...btn('#9F5CFF', true),
            fontSize: 11,
            padding: '6px 14px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}
        >
          ⚙️ Actions ▾
        </button>

        {menuOpen && (
          <div style={{
            position: 'absolute',
            right: 18,
            top: '70%',
            background: '#101428',
            border: '1px solid rgba(159,92,255,0.25)',
            borderRadius: 8,
            boxShadow: '0 10px 25px rgba(0,0,0,0.6)',
            zIndex: 100,
            minWidth: 160,
            padding: '6px 0',
          }}>
            <button 
              onClick={() => { setSelectedBookingDetails(b); setMenuOpen(false); }} 
              className="bkg-menu-item"
            >
              👁 Details
            </button>

            <button 
              onClick={() => { onEditBooking && onEditBooking(b); setMenuOpen(false); }} 
              className="bkg-menu-item"
              style={{ color: '#9F5CFF' }}
            >
              ✏️ Edit Booking
            </button>
            
            <button 
              onClick={() => { openExtras(b); setMenuOpen(false); }} 
              className="bkg-menu-item"
            >
              📋 Extras
            </button>

            <button 
              onClick={() => { openAssignModal && openAssignModal(b); setMenuOpen(false); }} 
              className="bkg-menu-item"
              style={{ color: '#00D2FF' }}
            >
              👤 Assign Organizer
            </button>

            {b.Contract?.pdf_url && (
              <a 
                href={b.Contract.pdf_url} 
                target="_blank" 
                rel="noreferrer" 
                onClick={() => setMenuOpen(false)}
                className="bkg-menu-item"
                style={{ color: b.Contract?.signed_at ? '#27C85A' : '#a0a0b8' }}
              >
                {b.Contract?.signed_at ? '✍ Signed PDF' : '⬇ Download Contract'}
              </a>
            )}

            {b.status === 'payment_received' && (
              <button 
                onClick={() => { markComplete(b.id); setMenuOpen(false); }} 
                className="bkg-menu-item"
                style={{ color: '#27C85A' }}
              >
                ✓ Confirm Booking
              </button>
            )}

            {b.status !== 'cancelled' && (
              <button 
                onClick={() => { handleSendInvoice(); setMenuOpen(false); }} 
                disabled={sendingInvoice}
                className="bkg-menu-item"
                style={{ color: '#2d9cdb' }}
              >
                📧 {sendingInvoice ? 'Sending…' : 'Send Invoice'}
              </button>
            )}

            {parseFloat(b.amount_remaining || 0) > 0 && (
              <button 
                onClick={() => { handleRequestBalance(); setMenuOpen(false); }} 
                disabled={requesting}
                className="bkg-menu-item"
                style={{ color: '#f5a623' }}
              >
                💰 {requesting ? 'Sending…' : 'Request Balance'}
              </button>
            )}

            {['pending_signature', 'contract_signed'].includes(b.status) && (
              <button 
                onClick={() => { setDeclineModal(b.id); setDeclineMsg(''); setMenuOpen(false); }} 
                className="bkg-menu-item bkg-menu-item-danger"
              >
                ✕ Decline
              </button>
            )}

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '4px 0' }} />

            <button 
              onClick={() => { removeBooking(b.id); setMenuOpen(false); }} 
              className="bkg-menu-item bkg-menu-item-danger"
            >
              🗑 Delete
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function BookingsTab({ setPendingCount }) {
  const { socket } = useSocket();
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [declineModal, setDeclineModal] = useState(null);
  const [declineMsg, setDeclineMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Extras modal state
  const [extrasModal, setExtrasModal] = useState(null);   // booking object
  const [extrasItems, setExtrasItems] = useState([]);      // [{ name, quantity, unit_price }]
  const [extrasNewRow, setExtrasNewRow] = useState({ name: '', quantity: 1, unit_price: '' });
  const [extrasSaving, setExtrasSaving] = useState(false);
  const [extrasInvoicing, setExtrasInvoicing] = useState(false);
  const [extrasMsg, setExtrasMsg] = useState('');
  const [systemAddons, setSystemAddons] = useState([]);
  const [systemBooths, setSystemBooths] = useState([]);
  const [systemBackdrops, setSystemBackdrops] = useState([]);
  const [selectedBookingDetails, setSelectedBookingDetails] = useState(null);

  const [sortBy, setSortBy] = useState('event_date');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Organizer assignment modal state
  const [assignModal, setAssignModal] = useState(null); // booking
  const [organizersList, setOrganizersList] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [scheduledHours, setScheduledHours] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState('');

  const openAssignModal = async (booking) => {
    setAssignModal(booking);
    setSelectedOrgId(booking.organizer_id ? String(booking.organizer_id) : '');
    setScheduledHours(booking.scheduled_hours ? String(booking.scheduled_hours) : (booking.duration_hours ? String(booking.duration_hours) : '4'));
    setAdminNotes(booking.admin_notes || '');
    setAssignError('');
    setAssignLoading(true);
    try {
      const res = await ax().get(`${API}/api/admin/organizers`);
      setOrganizersList(res.data.organizers || []);
    } catch (err) {
      console.error('Failed to load organizers', err);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrgId) {
      setAssignError('Please select an event organizer');
      return;
    }
    setAssignSaving(true);
    setAssignError('');
    try {
      await ax().post(`${API}/api/admin/bookings/${assignModal.id}/assign`, {
        organizer_id: parseInt(selectedOrgId),
        scheduled_hours: scheduledHours ? parseFloat(scheduledHours) : null,
        admin_notes: adminNotes
      });
      setAssignModal(null);
      load();
    } catch (err) {
      setAssignError(err.response?.data?.error || 'Failed to assign organizer');
    } finally {
      setAssignSaving(false);
    }
  };

  // Complete editing state
  const [isEditingBooking, setIsEditingBooking] = useState(false);
  const [editCustomerName, setEditCustomerName] = useState('');
  const [editCustomerEmail, setEditCustomerEmail] = useState('');
  const [editPhoneNumber, setEditPhoneNumber] = useState('');
  const [editEventDate, setEditEventDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editDurationHours, setEditDurationHours] = useState(2);
  const [editEndTime, setEditEndTime] = useState('');
  const [editVenueName, setEditVenueName] = useState('');
  const [editVenueAddress, setEditVenueAddress] = useState('');
  const [editBoothType, setEditBoothType] = useState('');
  const [editPhotoStripStyle, setEditPhotoStripStyle] = useState('2×6 (Vertical)');
  const [editBackdropId, setEditBackdropId] = useState('');
  const [editStripWordings, setEditStripWordings] = useState('');
  const [editEventTheme, setEditEventTheme] = useState('');
  const [editIsTaxExempt, setEditIsTaxExempt] = useState(false);
  const [editSubtotal, setEditSubtotal] = useState('');
  const [editTax, setEditTax] = useState('');
  const [editQuotedPrice, setEditQuotedPrice] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const calcEndTime = (startTime, durationHours) => {
    if (!startTime) return '00:00';
    const parts = startTime.split(':');
    if (parts.length < 2) return '00:00';
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return '00:00';
    const totalMins = h * 60 + m + Math.round((parseFloat(durationHours) || 0) * 60);
    const endH = Math.floor(totalMins / 60) % 24;
    const endM = totalMins % 60;
    return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
  };

  const populateEditForm = (b) => {
    if (!b) return;
    const isExempt = Boolean(b.is_tax_exempt);
    const totalVal = parseFloat(b.quoted_price || 0);
    const subtotalVal = (b.subtotal !== null && b.subtotal !== undefined && parseFloat(b.subtotal) > 0)
      ? parseFloat(b.subtotal)
      : (isExempt ? totalVal : parseFloat((totalVal / 1.0825).toFixed(2)));
    const taxVal = isExempt
      ? 0
      : ((b.tax !== null && b.tax !== undefined)
        ? parseFloat(b.tax)
        : parseFloat((totalVal - subtotalVal).toFixed(2)));

    setEditCustomerName(b.customer?.name || '');
    setEditCustomerEmail(b.customer?.email || '');
    setEditPhoneNumber(b.phone_number || '');
    setEditEventDate(b.event_date || '');
    const st = b.start_time || '18:00';
    const dur = b.duration_hours || 2;
    setEditStartTime(st);
    setEditDurationHours(dur);
    setEditEndTime(b.end_time || calcEndTime(st, dur));
    setEditVenueName(b.venue_name || '');
    setEditVenueAddress(b.venue_address || '');
    setEditBoothType(b.booth_type || '');
    setEditPhotoStripStyle(b.photo_strip_style || '2×6 (Vertical)');
    setEditBackdropId(b.backdrop_id ? String(b.backdrop_id) : (b.backdrop?.id ? String(b.backdrop.id) : ''));
    setEditStripWordings(b.strip_wordings || '');
    setEditEventTheme(b.event_theme_description || '');
    setEditIsTaxExempt(isExempt);
    setEditSubtotal(subtotalVal ? subtotalVal.toFixed(2) : '0.00');
    setEditTax(taxVal ? taxVal.toFixed(2) : '0.00');
    setEditQuotedPrice(totalVal ? totalVal.toFixed(2) : '0.00');
    setEditError('');
  };

  const startEditingBooking = (b) => {
    setSelectedBookingDetails(b);
    populateEditForm(b);
    setIsEditingBooking(true);
  };

  const handleStartTimeChange = (val) => {
    setEditStartTime(val);
    setEditEndTime(calcEndTime(val, editDurationHours));
  };

  const handleDurationChange = (val) => {
    setEditDurationHours(val);
    setEditEndTime(calcEndTime(editStartTime, val));
  };

  const handleSubtotalChange = (val) => {
    setEditSubtotal(val);
    const numSub = parseFloat(val);
    if (isNaN(numSub)) return;
    if (editIsTaxExempt) {
      setEditTax('0.00');
      setEditQuotedPrice(numSub.toFixed(2));
    } else {
      const calcTax = parseFloat((numSub * 0.0825).toFixed(2));
      setEditTax(calcTax.toFixed(2));
      setEditQuotedPrice((numSub + calcTax).toFixed(2));
    }
  };

  const handleTaxExemptToggle = (checked) => {
    setEditIsTaxExempt(checked);
    const numSub = parseFloat(editSubtotal) || 0;
    if (checked) {
      setEditTax('0.00');
      setEditQuotedPrice(numSub.toFixed(2));
    } else {
      const calcTax = parseFloat((numSub * 0.0825).toFixed(2));
      setEditTax(calcTax.toFixed(2));
      setEditQuotedPrice((numSub + calcTax).toFixed(2));
    }
  };

  useEffect(() => {
    if (selectedBookingDetails && !isEditingBooking) {
      populateEditForm(selectedBookingDetails);
    }
  }, [selectedBookingDetails]);

  useEffect(() => {
    ax().get(`${API}/api/addons`)
      .then(res => setSystemAddons(res.data.addons || []))
      .catch(() => {});
    ax().get(`${API}/api/booths`)
      .then(res => setSystemBooths(res.data.booths || []))
      .catch(() => {});
    ax().get(`${API}/api/backdrops`)
      .then(res => setSystemBackdrops(res.data.backdrops || []))
      .catch(() => {});
  }, []);

  const handleSelectSystemAddon = (addonId) => {
    if (!addonId) return;
    const addon = systemAddons.find(a => a.id === parseInt(addonId));
    if (addon) {
      setExtrasNewRow({
        name: addon.name,
        quantity: 1,
        unit_price: parseFloat(addon.price) || 0
      });
    }
  };

  const getAddonsList = (booking) => {
    const list = [];
    let sel = booking.selected_addons || [];
    if (typeof sel === 'string') {
      try { sel = JSON.parse(sel); } catch (_) { sel = []; }
    }
    if (sel && Array.isArray(sel)) {
      sel.forEach(a => {
        if (a.type === 'boolean') {
          list.push(a.name);
        } else {
          list.push(`${a.name} (x${a.quantity})`);
        }
      });
    } else {
      if (booking.addon_scrapbook) list.push('Scrapbook');
      if (parseInt(booking.addon_fridge_qty) > 0) list.push(`Fridge Magnets (${booking.addon_fridge_qty} qty)`);
      if (booking.addon_red_carpet) list.push('Red Carpet');
      if (booking.addon_green_screen) list.push('Green Screen');
      if (parseInt(booking.addon_extra_hours) > 0) list.push(`Extra Hours (+${booking.addon_extra_hours}h)`);
    }
    return list.length > 0 ? list.join(', ') : 'None';
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: filter, search, page, limit: 15, sortBy, sortOrder };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const { data } = await ax().get(`${API}/api/admin/bookings`, { params });
      setBookings(data.bookings);
      setTotal(data.total);
    } catch {}
    setLoading(false);
  }, [filter, search, page, startDate, endDate, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
    setPage(1);
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!socket) return;
    const h = () => { load(); if (setPendingCount) setPendingCount(n => n + 1); };
    socket.on('new_booking', h);
    return () => socket.off('new_booking', h);
  }, [socket, load]);

  const decline = async () => {
    await ax().patch(`${API}/api/admin/bookings/${declineModal}/decline`, { admin_message: declineMsg });
    setDeclineModal(null); setDeclineMsg('');
    load();
    if (setPendingCount) setPendingCount(n => Math.max(0, n - 1));
  };

  const removeBooking = (id) => {
    setConfirmModal({
      title: "Delete Booking Request",
      message: "Are you sure you want to permanently delete this booking? This will remove all contract records and cannot be undone.",
      confirmText: "Yes, Delete",
      confirmColor: "#E34364",
      onConfirm: async () => {
        try {
          await ax().delete(`${API}/api/admin/bookings/${id}`);
          load();
          if (setPendingCount) setPendingCount(n => Math.max(0, n - 1));
        } catch (err) {
          alert(err.response?.data?.error || "Failed to delete booking");
        }
      }
    });
  };

  const markComplete = (id) => {
    setConfirmModal({
      title: "Confirm Booking",
      message: "Confirm this booking? This means you've verified payment and are ready to serve this event.",
      confirmText: "Yes, Confirm",
      confirmColor: "#27C85A",
      onConfirm: async () => {
        try {
          await ax().patch(`${API}/api/admin/bookings/${id}/confirm`);
          load();
        } catch (err) {
          alert(err.response?.data?.error || 'Failed to confirm booking');
        }
      }
    });
  };

  const requestBalance = async (booking) => {
    try {
      const res = await ax().post(`${API}/api/admin/bookings/${booking.id}/request-balance`);
      alert(res.data.message || "Balance payment request sent!");
    } catch (err) {
      alert(err.response?.data?.error || "Failed to send balance payment request");
    }
  };

  const sendInvoice = async (booking) => {
    try {
      const res = await ax().post(`${API}/api/admin/bookings/${booking.id}/send-invoice`);
      alert(res.data.message || "Booking invoice sent!");
    } catch (err) {
      alert(err.response?.data?.error || "Failed to send booking invoice");
    }
  };

  const handleSaveEditedBooking = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError('');
    try {
      const payload = {
        customer_name: editCustomerName,
        customer_email: editCustomerEmail,
        phone_number: editPhoneNumber,
        event_date: editEventDate,
        start_time: editStartTime,
        end_time: editEndTime,
        duration_hours: editDurationHours ? parseFloat(editDurationHours) : undefined,
        venue_name: editVenueName,
        venue_address: editVenueAddress,
        booth_type: editBoothType,
        photo_strip_style: editPhotoStripStyle,
        backdrop_id: editBackdropId ? parseInt(editBackdropId) : null,
        strip_wordings: editStripWordings,
        event_theme_description: editEventTheme,
        is_tax_exempt: editIsTaxExempt,
        subtotal: editSubtotal !== '' ? parseFloat(editSubtotal) : undefined,
        tax: editTax !== '' ? parseFloat(editTax) : undefined,
        quoted_price: editQuotedPrice !== '' ? parseFloat(editQuotedPrice) : undefined,
      };

      const res = await ax().patch(`${API}/api/admin/bookings/${selectedBookingDetails.id}`, payload);
      const updated = res.data.booking;

      setSelectedBookingDetails(prev => ({
        ...prev,
        ...updated,
        customer: {
          ...(prev.customer || {}),
          name: editCustomerName,
          email: editCustomerEmail || prev.customer?.email,
        },
        backdrop: systemBackdrops.find(b => b.id === parseInt(editBackdropId)) || (editBackdropId ? prev.backdrop : null),
      }));
      setIsEditingBooking(false);
      load();
      alert(res.data.message || 'Booking updated successfully!');
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update booking details');
    } finally {
      setEditSaving(false);
    }
  };

  const openExtras = (booking) => {
    setExtrasModal(booking);
    
    let items = booking.booking_extras || [];
    if (typeof items === 'string') {
      try { items = JSON.parse(items); } catch (_) { items = []; }
    }
    if (!Array.isArray(items)) items = [];

    setExtrasItems(items);
    setExtrasNewRow({ name: '', quantity: 1, unit_price: '' });
    setExtrasMsg('');
  };

  const addExtrasRow = () => {
    const { name, quantity, unit_price } = extrasNewRow;
    if (!name.trim() || unit_price === '') return;
    const qty = parseInt(quantity) || 1;
    const price = parseFloat(unit_price) || 0;
    setExtrasItems(prev => [...prev, { name: name.trim(), quantity: qty, unit_price: price, total: parseFloat((qty * price).toFixed(2)) }]);
    setExtrasNewRow({ name: '', quantity: 1, unit_price: '' });
  };

  const removeExtrasRow = (idx) => setExtrasItems(prev => prev.filter((_, i) => i !== idx));

  const saveExtras = async () => {
    if (!extrasModal) return;
    setExtrasSaving(true); setExtrasMsg('');
    try {
      await ax().post(`${API}/api/admin/bookings/${extrasModal.id}/extras`, { items: extrasItems });
      setExtrasMsg('✅ Extras saved!');
      load();
    } catch { setExtrasMsg('❌ Failed to save extras.'); }
    setExtrasSaving(false);
  };

  const sendExtrasInvoice = async () => {
    if (!extrasModal) return;
    if (extrasItems.length === 0) { setExtrasMsg('⚠️ Save items first before sending invoice.'); return; }
    setExtrasInvoicing(true); setExtrasMsg('');
    try {
      await saveExtras();
      await ax().post(`${API}/api/admin/bookings/${extrasModal.id}/extras/invoice`);
      setExtrasMsg('📧 Invoice sent to customer!');
    } catch { setExtrasMsg('❌ Failed to send invoice.'); }
    setExtrasInvoicing(false);
  };

  const extrasTotal = extrasItems.reduce((s, it) => s + (parseFloat(it.total) || parseFloat(it.unit_price) * parseInt(it.quantity || 1)), 0);

  return (
    <div>
      <style>{`
        .bkg-menu-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          background: none;
          border: none;
          color: #a0a0b8;
          padding: 8px 14px;
          text-align: left;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.15s;
          box-sizing: border-box;
        }
        .bkg-menu-item:hover {
          background: rgba(159,92,255,0.15);
          color: #fff;
        }
        .bkg-menu-item-danger {
          color: #E34364 !important;
        }
        .bkg-menu-item-danger:hover {
          background: rgba(227,67,100,0.15) !important;
          color: #E34364 !important;
        }
      `}</style>
      {/* Search + filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name or date…"
          style={{ flex: 1, minWidth: 200, ...inp(false) }} />
        {TABS_FILTER.map(t => (
          <button key={t} onClick={() => { setFilter(t); setPage(1); }}
            style={{ ...btn(filter === t ? '#E34364' : 'rgba(255,255,255,0.1)', filter !== t), fontSize: 11, textTransform: 'capitalize', padding: '8px 14px' }}>
            {t.replace('_', ' ')}
          </button>
        ))}
      </div>
      {/* Event Date Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.04)', padding: 14, borderRadius: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#a0a0b8' }}>Event Date Range:</span>
          <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} style={{ padding: '6px 12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 12, outline: 'none' }} />
          <span style={{ fontSize: 12, color: '#555' }}>to</span>
          <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} style={{ padding: '6px 12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 12, outline: 'none' }} />
        </div>
        {(startDate || endDate) && (
          <button onClick={() => { setStartDate(''); setEndDate(''); setPage(1); }} style={{ ...btn('rgba(255,255,255,0.05)', true), border: '1px solid rgba(255,255,255,0.1)', padding: '6px 12px', fontSize: 11, color: '#a0a0b8', borderRadius: 8, cursor: 'pointer' }}>
            Clear Dates
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#a0a0b8', fontSize: 14 }}>Loading booking records…</div>
      ) : bookings.length === 0 ? (
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 40, margin: '0 0 12px' }}>📅</p>
          <p style={{ color: '#a0a0b8', fontSize: 14, fontWeight: 600, margin: 0 }}>No bookings found.</p>
          <p style={{ color: '#555', fontSize: 12, margin: '6px 0 0' }}>Bookings matching your criteria will appear here.</p>
        </div>
      ) : (
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 1000 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.01)' }}>
                  <th
                    style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('created_at')}
                  >
                    Booking ID {sortBy === 'created_at' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                  </th>
                  <th style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Customer Info
                  </th>
                  <th
                    style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('event_date')}
                  >
                    Event Details {sortBy === 'event_date' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                  </th>
                  <th style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Venue
                  </th>
                  <th
                    style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('quoted_price')}
                  >
                    Service & Price {sortBy === 'quoted_price' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                  </th>
                  <th style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Status
                  </th>
                  <th style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {bookings.map(b => (
                  <BookingRow
                    key={b.id}
                    b={b}
                    getAddonsList={getAddonsList}
                    setSelectedBookingDetails={setSelectedBookingDetails}
                    openExtras={openExtras}
                    markComplete={markComplete}
                    removeBooking={removeBooking}
                    setDeclineModal={setDeclineModal}
                    setDeclineMsg={setDeclineMsg}
                    requestBalance={requestBalance}
                    sendInvoice={sendInvoice}
                    onEditBooking={startEditingBooking}
                    openAssignModal={openAssignModal}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {total > 15 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {page > 1 && <button onClick={() => setPage(p => p - 1)} style={btn('#9F5CFF', true)}>← Prev</button>}
          <span style={{ color: '#a0a0b8', fontSize: 13, alignSelf: 'center' }}>Page {page}</span>
          {page * 15 < total && <button onClick={() => setPage(p => p + 1)} style={btn('#9F5CFF', true)}>Next →</button>}
        </div>
      )}

      {/* Decline modal */}
      {declineModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#101428', borderRadius: 16, padding: 28, border: '1px solid rgba(227,67,100,0.3)', width: '100%', maxWidth: 420 }}>
            <h3 style={{ margin: '0 0 16px', color: '#E34364' }}>Decline Booking</h3>
            <textarea value={declineMsg} onChange={e => setDeclineMsg(e.target.value)}
              placeholder="Optional message to customer…"
              style={{ ...inp(false), height: 100, resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={() => setDeclineModal(null)} style={btn('#555', true)}>Cancel</button>
              <button onClick={decline} style={btn('#E34364')}>Confirm Decline</button>
            </div>
          </div>
        </div>
      )}
      {/* Custom Confirmation Modal */}
      {confirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#101428', borderRadius: 16, padding: 28, border: `1px solid ${confirmModal.confirmColor || '#E34364'}44`, width: '100%', maxWidth: 440, boxShadow: `0 10px 30px rgba(0,0,0,0.5)` }}>
            <h3 style={{ margin: '0 0 12px', color: '#fff', fontSize: 18, fontWeight: 700 }}>{confirmModal.title}</h3>
            <p style={{ margin: '0 0 24px', color: '#a0a0b8', fontSize: 14, lineHeight: '1.5' }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmModal(null)} style={{ ...btn('#555', true), padding: '10px 20px', fontSize: 13 }}>Cancel</button>
              <button onClick={async () => {
                await confirmModal.onConfirm();
                setConfirmModal(null);
              }} style={{ ...btn(confirmModal.confirmColor), padding: '10px 24px', fontSize: 13 }}>{confirmModal.confirmText}</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Extras Modal ──────────────────────────────────────────── */}
      {extrasModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)', zIndex: 400, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}>
          <div style={{ background: '#101428', borderRadius: 20, padding: 28, border: '1px solid rgba(245,166,35,0.3)', width: '100%', maxWidth: 580, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, color: '#f5a623', fontSize: 18, fontWeight: 800 }}>📋 Extras Manager</h3>
                <p style={{ margin: '4px 0 0', color: '#a0a0b8', fontSize: 12 }}>{extrasModal.customer?.name} · {extrasModal.event_date} · {extrasModal.booth_type}</p>
              </div>
              <button onClick={() => setExtrasModal(null)} style={{ background: 'none', border: 'none', color: '#a0a0b8', fontSize: 22, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
            </div>

            {/* Existing items */}
            {extrasItems.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ margin: '0 0 10px', fontSize: 12, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Added Items</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {extrasItems.map((it, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 14px' }}>
                      <span style={{ flex: 2, fontSize: 13, color: '#e0e0e0', fontWeight: 600 }}>{it.name}</span>
                      <span style={{ flex: '0 0 40px', fontSize: 12, color: '#a0a0b8', textAlign: 'center' }}>×{it.quantity}</span>
                      <span style={{ flex: '0 0 70px', fontSize: 12, color: '#a0a0b8', textAlign: 'right' }}>${parseFloat(it.unit_price).toFixed(2)}/ea</span>
                      <span style={{ flex: '0 0 70px', fontSize: 13, color: '#27C85A', fontWeight: 700, textAlign: 'right' }}>${parseFloat(it.total || it.unit_price * it.quantity).toFixed(2)}</span>
                      <button onClick={() => removeExtrasRow(idx)} style={{ background: 'none', border: 'none', color: '#E34364', fontSize: 16, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, padding: '8px 14px', background: 'rgba(245,166,35,0.08)', borderRadius: 8, border: '1px solid rgba(245,166,35,0.2)' }}>
                  <span style={{ fontSize: 14, color: '#f5a623', fontWeight: 800 }}>Total: ${extrasTotal.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Add new item row */}
            <p style={{ margin: '0 0 10px', fontSize: 12, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Add Item</p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <select
                onChange={e => handleSelectSystemAddon(e.target.value)}
                style={{ ...inp(false), flex: '1 1 160px', fontSize: 13, color: '#fff' }}
                defaultValue=""
              >
                <option value="" disabled style={{ background: '#101428' }}>Select standard addon...</option>
                {systemAddons.map(a => (
                  <option key={a.id} value={a.id} style={{ background: '#101428' }}>
                    {a.name} (${parseFloat(a.price).toFixed(2)})
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <input
                placeholder="Item name…"
                value={extrasNewRow.name}
                onChange={e => setExtrasNewRow(r => ({ ...r, name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addExtrasRow()}
                style={{ ...inp(false), flex: '2 1 160px', fontSize: 13 }}
              />
              <input
                placeholder="Qty"
                type="number" min="1"
                value={extrasNewRow.quantity}
                onChange={e => setExtrasNewRow(r => ({ ...r, quantity: e.target.value }))}
                style={{ ...inp(false), flex: '0 1 60px', fontSize: 13 }}
              />
              <input
                placeholder="Price $"
                type="number" min="0" step="0.01"
                value={extrasNewRow.unit_price}
                onChange={e => setExtrasNewRow(r => ({ ...r, unit_price: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addExtrasRow()}
                style={{ ...inp(false), flex: '0 1 100px', fontSize: 13 }}
              />
              <button onClick={addExtrasRow} style={{ ...btn('#9F5CFF'), padding: '10px 16px', fontSize: 13, whiteSpace: 'nowrap' }}>+ Add</button>
            </div>

            {/* Status message */}
            {extrasMsg && (
              <p style={{ margin: '0 0 14px', fontSize: 13, color: extrasMsg.startsWith('✅') || extrasMsg.startsWith('📧') ? '#27C85A' : extrasMsg.startsWith('⚠️') ? '#f5a623' : '#E34364' }}>{extrasMsg}</p>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => setExtrasModal(null)} style={{ ...btn('#555', true), flex: 1 }}>Close</button>
              <button onClick={saveExtras} disabled={extrasSaving} style={{ ...btn('#9F5CFF'), flex: 1 }}>
                {extrasSaving ? 'Saving…' : '💾 Save Extras'}
              </button>
              <button onClick={sendExtrasInvoice} disabled={extrasInvoicing || extrasItems.length === 0} style={{ ...btn('#f5a623'), flex: 2, opacity: extrasItems.length === 0 ? 0.5 : 1 }}>
                {extrasInvoicing ? 'Sending…' : '📧 Send Invoice to Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Details / Edit Modal */}
      {selectedBookingDetails && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#101428', borderRadius: 20, padding: 28, border: '1px solid rgba(159,92,255,0.3)', width: '100%', maxWidth: isEditingBooking ? 680 : 560, boxShadow: '0 20px 50px rgba(0,0,0,0.5)', maxHeight: '90vh', overflowY: 'auto' }}>
            
            {isEditingBooking ? (
              <form onSubmit={handleSaveEditedBooking}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#9F5CFF', fontSize: 18, fontWeight: 700 }}>✏️ Edit Booking Details</h3>
                    <p style={{ margin: '4px 0 0', color: '#a0a0b8', fontSize: 12 }}>
                      BOOKING-#{String(selectedBookingDetails.id).padStart(5, '0')} · Modify customer, logistics, strip style & financials
                    </p>
                  </div>
                  <button type="button" onClick={() => setIsEditingBooking(false)} style={{ background: 'none', border: 'none', color: '#a0a0b8', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                </div>

                {editError && (
                  <div style={{ padding: '10px 14px', background: 'rgba(227,67,100,0.1)', border: '1px solid rgba(227,67,100,0.3)', borderRadius: 10, color: '#E34364', fontSize: 13, marginBottom: 16 }}>
                    ⚠️ {editError}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginBottom: 24 }}>
                  
                  {/* 1. Customer Information */}
                  <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 16 }}>
                    <p style={{ margin: '0 0 12px', fontSize: 11, color: '#9F5CFF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>👤</span> Customer Information
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Full Name *</label>
                        <input value={editCustomerName} onChange={e => setEditCustomerName(e.target.value)} required style={inp(false)} placeholder="Client Name" />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Email Address *</label>
                        <input type="email" value={editCustomerEmail} onChange={e => setEditCustomerEmail(e.target.value)} required style={inp(false)} placeholder="client@example.com" />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Phone Number</label>
                        <input value={editPhoneNumber} onChange={e => setEditPhoneNumber(e.target.value)} style={inp(false)} placeholder="(555) 000-0000" />
                      </div>
                    </div>
                  </div>

                  {/* 2. Event Timing & Venue Location */}
                  <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 16 }}>
                    <p style={{ margin: '0 0 12px', fontSize: 11, color: '#9F5CFF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>📅</span> Event Schedule & Location
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Event Date *</label>
                        <input type="date" value={editEventDate} onChange={e => setEditEventDate(e.target.value)} required style={inp(false)} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Start Time</label>
                        <input type="time" value={editStartTime} onChange={e => handleStartTimeChange(e.target.value)} style={inp(false)} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Duration (Hrs)</label>
                        <input type="number" step="0.5" min="1" max="24" value={editDurationHours} onChange={e => handleDurationChange(e.target.value)} style={inp(false)} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>End Time</label>
                        <input type="time" value={editEndTime} onChange={e => setEditEndTime(e.target.value)} style={inp(false)} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Venue / Facility Name</label>
                        <input value={editVenueName} onChange={e => setEditVenueName(e.target.value)} placeholder="e.g. Grand Ballroom" style={inp(false)} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Venue Street Address</label>
                        <input value={editVenueAddress} onChange={e => setEditVenueAddress(e.target.value)} placeholder="123 Celebration Blvd, City, TX" style={inp(false)} />
                      </div>
                    </div>
                  </div>

                  {/* 3. Booth, Strip Style & Creative */}
                  <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 16 }}>
                    <p style={{ margin: '0 0 12px', fontSize: 11, color: '#9F5CFF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>📸</span> Booth, Photo Strip & Creative Details
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Booth Type</label>
                        {systemBooths.length > 0 ? (
                          <select value={editBoothType} onChange={e => setEditBoothType(e.target.value)} style={{ ...inp(false), color: '#fff' }}>
                            {!systemBooths.some(sb => sb.name === editBoothType) && editBoothType && (
                              <option value={editBoothType}>{editBoothType}</option>
                            )}
                            {systemBooths.map(b => (
                              <option key={b.id} value={b.name} style={{ background: '#101428' }}>{b.name}</option>
                            ))}
                          </select>
                        ) : (
                          <input value={editBoothType} onChange={e => setEditBoothType(e.target.value)} style={inp(false)} />
                        )}
                      </div>

                      {/* Photo Strip Style Selector */}
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#f5a623', fontWeight: 700, marginBottom: 4 }}>
                          🎞️ Photo Strip Size / Style
                        </label>
                        <select 
                          value={editPhotoStripStyle} 
                          onChange={e => setEditPhotoStripStyle(e.target.value)}
                          style={{ ...inp(false), color: '#fff', borderColor: 'rgba(245,166,35,0.4)', background: 'rgba(245,166,35,0.05)' }}
                        >
                          <option value="2×6 (Vertical)" style={{ background: '#101428' }}>2×6 (Vertical)</option>
                          <option value="4×6 (Horizontal)" style={{ background: '#101428' }}>4×6 (Horizontal)</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Backdrop</label>
                        <select value={editBackdropId} onChange={e => setEditBackdropId(e.target.value)} style={{ ...inp(false), color: '#fff' }}>
                          <option value="" style={{ background: '#101428' }}>None / TBD</option>
                          {systemBackdrops.map(bd => (
                            <option key={bd.id} value={String(bd.id)} style={{ background: '#101428' }}>
                              {bd.name} {bd.category ? `(${bd.category})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Strip Wordings / Print Text</label>
                        <input value={editStripWordings} onChange={e => setEditStripWordings(e.target.value)} placeholder="e.g. Sarah & Alex · October 24, 2026" style={inp(false)} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Event Theme / Special Instructions</label>
                        <textarea value={editEventTheme} onChange={e => setEditEventTheme(e.target.value)} placeholder="Describe color palette, theme, or custom backdrop requests..." style={{ ...inp(false), height: 60, resize: 'vertical', fontFamily: 'inherit' }} />
                      </div>
                    </div>
                  </div>

                  {/* 4. Financials & Tax-Free Setting */}
                  <div style={{ background: 'rgba(159,92,255,0.04)', border: '1px solid rgba(159,92,255,0.2)', borderRadius: 12, padding: 16 }}>
                    <p style={{ margin: '0 0 12px', fontSize: 11, color: '#9F5CFF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>💰</span> Pricing, Tax-Free & Quotation
                    </p>

                    {/* Tax-Free Toggle Banner */}
                    <div 
                      onClick={() => handleTaxExemptToggle(!editIsTaxExempt)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        background: editIsTaxExempt ? 'rgba(39,200,90,0.12)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${editIsTaxExempt ? 'rgba(39,200,90,0.4)' : 'rgba(255,255,255,0.08)'}`,
                        borderRadius: 10,
                        cursor: 'pointer',
                        marginBottom: 14,
                        transition: 'all 0.2s',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: editIsTaxExempt ? '#27C85A' : '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>{editIsTaxExempt ? '✅' : '⚪'}</span>
                          <span>Tax-Free / Tax-Exempt Booking</span>
                          {editIsTaxExempt && (
                            <span style={{ fontSize: 10, background: '#27C85A', color: '#000', padding: '1px 6px', borderRadius: 4, fontWeight: 800 }}>
                              $0.00 TAX
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2 }}>
                          {editIsTaxExempt 
                            ? 'Sales tax waived ($0.00). Invoice, contract, and quotes will reflect Tax-Free status.' 
                            : 'Click to waive 8.25% sales tax for qualifying tax-exempt or non-profit clients.'}
                        </div>
                      </div>
                      <input 
                        type="checkbox" 
                        checked={editIsTaxExempt} 
                        onChange={e => handleTaxExemptToggle(e.target.checked)}
                        style={{ width: 18, height: 18, accentColor: '#27C85A', cursor: 'pointer' }}
                        onClick={e => e.stopPropagation()}
                      />
                    </div>

                    {/* Pricing Inputs */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>Subtotal ($)</label>
                        <input 
                          type="number" 
                          step="0.01" 
                          value={editSubtotal} 
                          onChange={e => handleSubtotalChange(e.target.value)} 
                          style={inp(false)} 
                          placeholder="0.00" 
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: editIsTaxExempt ? '#27C85A' : '#a0a0b8', fontWeight: 600, marginBottom: 4 }}>
                          {editIsTaxExempt ? 'Sales Tax (Exempt)' : 'Sales Tax ($)'}
                        </label>
                        <input 
                          type="number" 
                          step="0.01" 
                          value={editTax} 
                          onChange={e => setEditTax(e.target.value)} 
                          disabled={editIsTaxExempt}
                          style={{ 
                            ...inp(false), 
                            opacity: editIsTaxExempt ? 0.7 : 1,
                            color: editIsTaxExempt ? '#27C85A' : '#fff'
                          }} 
                          placeholder="0.00" 
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#27C85A', fontWeight: 700, marginBottom: 4 }}>
                          Total Quoted Price ($) *
                        </label>
                        <input 
                          type="number" 
                          step="0.01" 
                          value={editQuotedPrice} 
                          onChange={e => setEditQuotedPrice(e.target.value)} 
                          required 
                          style={{ ...inp(false), borderColor: 'rgba(39,200,90,0.4)', fontWeight: 700, color: '#27C85A' }} 
                          placeholder="0.00" 
                        />
                      </div>
                    </div>

                    <p style={{ margin: '10px 0 0', fontSize: 11, color: '#a0a0b8', fontStyle: 'italic' }}>
                      ℹ️ Saving automatically adjusts the remaining balance, updates invoice records, and refreshes the contract PDF if unsigned.
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <button type="button" onClick={() => setIsEditingBooking(false)} style={{ ...btn('#555', true), flex: 1, padding: '12px 0', borderRadius: 10 }}>Cancel</button>
                  <button type="submit" disabled={editSaving} style={{ ...btn('#9F5CFF'), flex: 2, padding: '12px 0', borderRadius: 10, fontWeight: 700 }}>
                    {editSaving ? 'Saving Changes…' : '💾 Save Booking Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 700 }}>Booking Details</h3>
                    <p style={{ margin: '4px 0 0', color: '#9F5CFF', fontSize: 12, fontWeight: 600 }}>
                      BOOKING-#{String(selectedBookingDetails.id).padStart(5, '0')} · {selectedBookingDetails.status.toUpperCase()}
                      {selectedBookingDetails.is_tax_exempt && (
                        <span style={{ marginLeft: 8, background: 'rgba(39,200,90,0.15)', color: '#27C85A', border: '1px solid rgba(39,200,90,0.3)', padding: '2px 6px', borderRadius: 4, fontSize: 10, textTransform: 'uppercase', fontWeight: 800 }}>
                          Tax Free
                        </span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedBookingDetails(null)}
                    style={{ background: 'none', border: 'none', color: '#a0a0b8', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>

                {/* Customer Info */}
                <p style={{ margin: '0 0 8px', fontSize: 11, color: '#a0a0b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Customer Info</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 13 }}>
                  <div>
                    <span style={{ color: '#555', display: 'block', fontSize: 10, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Name</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{selectedBookingDetails.customer?.name}</span>
                  </div>
                  <div>
                    <span style={{ color: '#555', display: 'block', fontSize: 10, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Contact</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{selectedBookingDetails.customer?.email}</span>
                    <span style={{ color: '#a0a0b8', display: 'block', fontSize: 11, marginTop: 1 }}>{selectedBookingDetails.phone_number || '—'}</span>
                  </div>
                </div>

                {/* Event Info */}
                <p style={{ margin: '0 0 8px', fontSize: 11, color: '#a0a0b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Event Details</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 13 }}>
                  <div>
                    <span style={{ color: '#555', display: 'block', fontSize: 10, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Date & Time</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>📅 {selectedBookingDetails.event_date}</span>
                    <span style={{ color: '#a0a0b8', display: 'block', fontSize: 11, marginTop: 1 }}>🕒 {selectedBookingDetails.start_time} – {selectedBookingDetails.end_time} ({selectedBookingDetails.duration_hours}h)</span>
                  </div>
                  <div>
                    <span style={{ color: '#555', display: 'block', fontSize: 10, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Venue & Strip Wordings</span>
                    {selectedBookingDetails.venue_name && <span style={{ color: '#fff', fontWeight: 600, display: 'block' }}>🏛️ {selectedBookingDetails.venue_name}</span>}
                    {selectedBookingDetails.strip_wordings && (
                      <span style={{ color: '#f5a623', fontWeight: 600, display: 'block', fontSize: 12 }}>
                        💬 "{selectedBookingDetails.strip_wordings}"
                      </span>
                    )}
                    <span style={{ color: '#a0a0b8', display: 'block', fontSize: 11, marginTop: 1 }}>📍 {selectedBookingDetails.venue_address || '—'}</span>
                  </div>
                </div>

                {/* Services & Theme */}
                <p style={{ margin: '0 0 8px', fontSize: 11, color: '#a0a0b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Services & Style</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#a0a0b8' }}>Booth Type:</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{selectedBookingDetails.booth_type}</span>
                  </div>
                  
                  {/* Photo Strip Size clearly visible */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#a0a0b8' }}>Photo Strip Size:</span>
                    <span style={{ 
                      color: '#f5a623', 
                      fontWeight: 700, 
                      background: 'rgba(245,166,35,0.1)', 
                      border: '1px solid rgba(245,166,35,0.25)', 
                      padding: '2px 8px', 
                      borderRadius: 6,
                      fontSize: 12
                    }}>
                      🎞️ {selectedBookingDetails.photo_strip_style || '2×6 (Vertical)'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#a0a0b8' }}>Backdrop:</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{selectedBookingDetails.backdrop?.name || 'None/TBD'}</span>
                  </div>
                  {selectedBookingDetails.event_theme_description && (
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 8, marginTop: 4 }}>
                      <span style={{ color: '#555', display: 'block', fontSize: 10, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Theme / Description</span>
                      <span style={{ color: '#e0e0e0', fontStyle: 'italic' }}>"{selectedBookingDetails.event_theme_description}"</span>
                    </div>
                  )}
                </div>

                {/* Selected Addons Breakdown */}
                <p style={{ margin: '0 0 8px', fontSize: 11, color: '#a0a0b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Selected Add-ons</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                  {(() => {
                    let addons = selectedBookingDetails.selected_addons || [];
                    if (typeof addons === 'string') {
                      try { addons = JSON.parse(addons); } catch (_) { addons = []; }
                    }
                    if (!Array.isArray(addons) || addons.length === 0) {
                      return <p style={{ color: '#555', fontSize: 13, fontStyle: 'italic', margin: 0 }}>No add-ons selected for this booking.</p>;
                    }
                    return addons.map((it, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.03)', borderRadius: 10, padding: '10px 14px' }}>
                        <div>
                          <div style={{ fontSize: 13, color: '#e0e0e0', fontWeight: 600 }}>{it.name}</div>
                          {it.type !== 'boolean' && (
                            <div style={{ fontSize: 11, color: '#555', marginTop: 1 }}>
                              Qty: {it.quantity || 1} &nbsp;@&nbsp; ${parseFloat(it.price || 0).toFixed(2)}/ea
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: 14, color: '#fff', fontWeight: 700 }}>
                          ${parseFloat(it.total || 0).toFixed(2)}
                        </span>
                      </div>
                    ));
                  })()}
                </div>

                {/* Pricing Summary */}
                <p style={{ margin: '0 0 8px', fontSize: 11, color: '#a0a0b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Pricing Breakdown</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'rgba(159,92,255,0.08)', borderRadius: 12, border: '1px solid rgba(159,92,255,0.2)', padding: 14, marginBottom: 20, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#9F5CFF' }}>Base Rate (incl. hours):</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>${parseFloat(selectedBookingDetails.booth_base_rate || 0).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#9F5CFF' }}>Add-ons Total:</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>${parseFloat(selectedBookingDetails.addons_total || 0).toFixed(2)}</span>
                  </div>
                  {(() => {
                    const isExempt = Boolean(selectedBookingDetails.is_tax_exempt);
                    const totalVal = parseFloat(selectedBookingDetails.quoted_price || 0);
                    const subtotalVal = (selectedBookingDetails.subtotal && parseFloat(selectedBookingDetails.subtotal) > 0)
                      ? parseFloat(selectedBookingDetails.subtotal)
                      : (isExempt ? totalVal : parseFloat((totalVal / 1.0825).toFixed(2)));
                    const taxVal = isExempt
                      ? 0
                      : ((selectedBookingDetails.tax !== undefined && selectedBookingDetails.tax !== null)
                        ? parseFloat(selectedBookingDetails.tax)
                        : parseFloat((totalVal - subtotalVal).toFixed(2)));

                    return (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(159,92,255,0.1)', paddingTop: 6 }}>
                          <span style={{ color: '#9F5CFF' }}>Subtotal:</span>
                          <span style={{ color: '#fff', fontWeight: 600 }}>${subtotalVal.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#9F5CFF' }}>Sales Tax:</span>
                          {isExempt ? (
                            <span style={{ color: '#27C85A', fontWeight: 700, fontSize: 12, background: 'rgba(39,200,90,0.12)', border: '1px solid rgba(39,200,90,0.3)', padding: '2px 8px', borderRadius: 6 }}>
                              $0.00 (Tax Free / Exempt)
                            </span>
                          ) : (
                            <span style={{ color: '#fff', fontWeight: 600 }}>${taxVal.toFixed(2)} (8.25%)</span>
                          )}
                        </div>
                      </>
                    );
                  })()}
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(159,92,255,0.2)', paddingTop: 8, marginTop: 4 }}>
                    <span style={{ color: '#9F5CFF', fontWeight: 700 }}>Total Price:</span>
                    <span style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>${parseFloat(selectedBookingDetails.quoted_price || 0).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(159,92,255,0.1)', paddingTop: 8 }}>
                    <span style={{ color: '#27C85A' }}>Amount Paid ({selectedBookingDetails.payment_type || 'unpaid'}):</span>
                    <span style={{ color: '#27C85A', fontWeight: 700 }}>${parseFloat(selectedBookingDetails.amount_paid || 0).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#f5a623' }}>Amount Remaining:</span>
                    <span style={{ color: '#f5a623', fontWeight: 700 }}>${parseFloat(selectedBookingDetails.amount_remaining || 0).toFixed(2)}</span>
                  </div>
                </div>

                {/* Signature & Contract info */}
                {selectedBookingDetails.Contract && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 11, color: '#a0a0b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Contract & Signature</p>
                    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 12, padding: 14, fontSize: 13 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ color: '#555', display: 'block', fontSize: 10, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Signature Status</span>
                          <span style={{ color: selectedBookingDetails.Contract.signed_at ? '#27C85A' : '#f5a623', fontWeight: 600 }}>
                            {selectedBookingDetails.Contract.signed_at ? `Signed on ${new Date(selectedBookingDetails.Contract.signed_at).toLocaleDateString()}` : 'Awaiting Signature'}
                          </span>
                        </div>
                        {selectedBookingDetails.Contract.pdf_url && (
                          <a href={selectedBookingDetails.Contract.pdf_url} target="_blank" rel="noreferrer" style={{ ...btn('#9F5CFF', true), textDecoration: 'none', padding: '6px 12px', fontSize: 11 }}>
                            View Contract PDF
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => setSelectedBookingDetails(null)}
                    style={{ ...btn('#555', true), flex: 1, padding: '12px 0', borderRadius: 10, fontSize: 13 }}
                  >
                    Close Details
                  </button>
                  <button
                    type="button"
                    onClick={() => startEditingBooking(selectedBookingDetails)}
                    style={{ ...btn('#9F5CFF'), flex: 1, padding: '12px 0', borderRadius: 10, fontSize: 13, fontWeight: 700 }}
                  >
                    📝 Edit Details
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Assign Organizer Modal */}
      {assignModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 350,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16
        }}>
          <div style={{
            background: '#101428',
            borderRadius: 16,
            border: '1px solid rgba(0,210,255,0.3)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.7)',
            width: '100%',
            maxWidth: 480,
            padding: 28,
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#00D2FF' }}>👤</span> Assign Event Organizer
                </h3>
                <p style={{ margin: '4px 0 0', color: '#a0a0b8', fontSize: 12 }}>
                  Booking BKG-{String(assignModal.id).padStart(5, '0')} • {assignModal.event_date} ({assignModal.start_time} - {assignModal.end_time})
                </p>
              </div>
              <button
                onClick={() => setAssignModal(null)}
                style={{ background: 'none', border: 'none', color: '#a0a0b8', fontSize: 20, cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            {assignError && (
              <div style={{
                background: 'rgba(227,67,100,0.15)',
                border: '1px solid rgba(227,67,100,0.3)',
                borderRadius: 8,
                padding: '10px 14px',
                color: '#E34364',
                fontSize: 12,
                marginBottom: 16
              }}>
                {assignError}
              </div>
            )}

            {assignLoading ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#a0a0b8', fontSize: 13 }}>
                Loading event organizers…
              </div>
            ) : (
              <form onSubmit={handleAssignSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', fontWeight: 600, marginBottom: 6 }}>
                    Select Organizer *
                  </label>
                  <select
                    value={selectedOrgId}
                    onChange={e => setSelectedOrgId(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 8,
                      padding: '10px 14px',
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="" style={{ background: '#101428' }}>-- Choose Organizer --</option>
                    {organizersList.map(org => (
                      <option key={org.id} value={org.id} style={{ background: '#101428' }}>
                        {org.name} ({org.email}) {org.stats ? `• ${org.stats.completedEvents || 0} completed` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', fontWeight: 600, marginBottom: 6 }}>
                    Scheduled Working Hours
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={scheduledHours}
                    onChange={e => setScheduledHours(e.target.value)}
                    placeholder="e.g. 4.0"
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 8,
                      padding: '10px 14px',
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  <span style={{ fontSize: 11, color: '#7c7c9c', marginTop: 4, display: 'block' }}>
                    Scheduled time including setup, active booth time & teardown.
                  </span>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', fontWeight: 600, marginBottom: 6 }}>
                    Admin / Staff Briefing Notes
                  </label>
                  <textarea
                    rows={3}
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    placeholder="Special instructions for the staff (parking instructions, loading dock info, VIP requirements)..."
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 8,
                      padding: '10px 14px',
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => setAssignModal(null)}
                    style={{
                      flex: 1,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 8,
                      padding: '10px 0',
                      color: '#a0a0b8',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={assignSaving}
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #00D2FF 0%, #9F5CFF 100%)',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 0',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: assignSaving ? 'not-allowed' : 'pointer',
                      opacity: assignSaving ? 0.7 : 1
                    }}
                  >
                    {assignSaving ? 'Assigning…' : 'Confirm Assignment'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
